import * as React from 'react';
import { formatValue, HUNDRED_PCT, roundToHundreds } from '../../../utils/common';
import { DailyUsage, filterByEntityAndDatesUsage, Resource } from '../../../../store/ResourcesListStore';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { TimeType } from './common';
import { calculateSummary } from './Cell';
import { getWorkingHoursBetweenDates } from '../../../common/timeline/utils';
import { ViewType } from '../ResourceUsageGrid';
import { EntityType, mapServerEntityType } from '../../../../entities/common';
import { getDefaultCostRateTable } from '../../../../store/selectors/costRateTableSelectors';
import CellValue from './CellValue';
import { usePrevious } from '../../../utils/effects';
import { arraysEqual } from 'office-ui-fabric-react';
import { CalendarsState, getEntityCalendar } from '../../../../store/CalendarsListStore';

interface StateProps {
    resourcePlanningLevel: EntityType;
    defaultCostRateTableId?: string;
    calendars : CalendarsState;
}
interface OwnProps {
    resources: Resource[];
    entityIds: string[];
    entityType: EntityType;
    startDate: Date;
    finishDate: Date;
    timeType: TimeType[];
    viewType: ViewType;
}

type Props = StateProps & OwnProps;
interface State {
    plan: number;
    actual: number;
    capacity: number;
    availability: number;
    planPercent?: number;
    actualPercent?: number;
    capacityPercent?: number;
    availabilityPercent?: number;
    planFTE?: number;
    actualFTE?: number;
    capacityFTE?: number;
    availabilityFTE?: number;
}

const TotalUsageCell = (props: Props) => {
    const [state, setState] = React.useState<State>({plan: 0, actual: 0, capacity: 0, availability: 0});
    const prevProps = usePrevious(props);
    React.useEffect(        
        () => {
            if(!prevProps || !arraysEqual(props.entityIds, prevProps.entityIds)
                || props.calendars.byId !== prevProps.calendars.byId
                || props.calendars.archivedById !== prevProps.calendars.archivedById
                || props.resources !== prevProps.resources
                || props.startDate.getTime() !== prevProps.startDate.getTime()
                || props.finishDate.getTime() !== prevProps.finishDate.getTime()
                || props.entityType !== prevProps.entityType
                || props.resourcePlanningLevel !== prevProps.resourcePlanningLevel
            ){
                setState(buildState(props));
            }
        }, 
        [props]);

    const { timeType, viewType } = props;
    const { plan, actual, capacity, availability, planPercent, actualPercent, capacityPercent,
        availabilityPercent, planFTE, actualFTE, capacityFTE, availabilityFTE } = state;

    const unit = viewType === ViewType.Percent ? '%' : viewType === ViewType.FTE ? 'FTE' : 'hours';
    const planValue = viewType === ViewType.Percent ? planPercent : viewType === ViewType.FTE ? planFTE : plan;
    const actualValue = viewType === ViewType.Percent ? actualPercent : viewType === ViewType.FTE ? actualFTE : actual;
    const capacityValue = viewType === ViewType.Percent ? capacityPercent : viewType === ViewType.FTE ? capacityFTE : capacity;
    const availabilityValue = viewType === ViewType.Percent ? availabilityPercent : viewType === ViewType.FTE ? availabilityFTE : availability;

    return <div className="resource-usage-cell fill-space total" style={{ position: "relative" }}>
        <div className={`cell-content fill-space columns-${timeType.length}`}>
            {timeType.includes('plan') && <CellValue value={formatValue(planValue)} unit={unit} isEmpty={false} title='Planned' className='plan' />}
            {timeType.includes('actual') && <CellValue value={formatValue(actualValue)} unit={unit} isEmpty={false} title='Actual' className='actual' />}
            {timeType.includes('availability') && <CellValue value={formatValue(availabilityValue)} unit={unit} isEmpty={false} title='Availability'
                className='availability' />}
            {timeType.includes('capacity') && <CellValue value={formatValue(capacityValue)} unit={unit} isEmpty={false} title='Capacity' className='capacity' />}
        </div>
    </div>;
}

const buildState = (props: Props): State => {
    const total = props.resources.reduce((acc, resource) => {
        const calendar = getEntityCalendar(props.calendars, resource, resource.calendar.id);
        const usages = props.entityIds.reduce<DailyUsage[]>((entityAcc, entityId) =>
            [...entityAcc, ...filterByEntityAndDatesUsage(resource.usage, entityId, props.startDate, props.finishDate)], []);
        const plan = usages.reduce((sum, u) => sum + u.plannedHours, 0);
        const actual = usages.reduce((sum, u) => sum + u.actualHours, 0);
        const summary = calculateSummary(resource, props.startDate, props.finishDate, calendar, props.entityType, props.resourcePlanningLevel, props.defaultCostRateTableId);
        const fullFteHours = getWorkingHoursBetweenDates(props.startDate, props.finishDate, calendar);
        const toFte = (value: number) => fullFteHours ? value / fullFteHours : 0;

        return {
            plan: acc.plan + plan,
            actual: acc.actual + actual,
            capacity: acc.capacity + summary.capacityHours,
            availability: acc.availability + summary.availability,
            planFTE: acc.planFTE + toFte(plan),
            actualFTE: acc.actualFTE + toFte(actual),
            capacityFTE: acc.capacityFTE + toFte(summary.capacityHours),
            availabilityFTE: acc.availabilityFTE + toFte(summary.availability),
        };
    }, { plan: 0, actual: 0, capacity: 0, availability: 0, planFTE: 0, actualFTE: 0, capacityFTE: 0, availabilityFTE: 0 });

    const hours = { plan: total.plan, actual: total.actual };
    const { capacity, availability } = total;

    const planPercent = capacity ? Math.round(hours.plan / capacity * HUNDRED_PCT) : 0;
    const actualPercent = capacity ? Math.round(hours.actual / capacity * HUNDRED_PCT) : 0;
    const capacityPercent = capacity ? HUNDRED_PCT : 0;
    const availabilityPercent = capacity ? Math.round(availability / capacity * HUNDRED_PCT) : 0;
    
    const planFTE = roundToHundreds(total.planFTE);
    const actualFTE = roundToHundreds(total.actualFTE);
    const availabilityFTE = roundToHundreds(total.availabilityFTE);
    const capacityFTE = roundToHundreds(total.capacityFTE);

    return { ...hours, capacity, availability, planPercent, actualPercent, capacityPercent, availabilityPercent, planFTE, actualFTE, capacityFTE, availabilityFTE };
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    return {
        calendars: state.calendars,
        resourcePlanningLevel: mapServerEntityType[state.tenant.resourcePlanningSettings?.resourcePlanningLevel!]!,
        defaultCostRateTableId: getDefaultCostRateTable(state)?.id,
    };
}

export default connect(mapStateToProps)(TotalUsageCell);